import React, { useEffect, useState } from "react";
import {
  fetchAppleWalletPass,
  fetchGoogleWalletLink,
  fetchWalletCapabilities,
} from "../utils/api";
import HorizontalCard, { HorizontalCardData } from "./HorizontalCard";
import {
  detectPlatform,
  isInstallPromptAvailable,
  isStandalone,
  promptInstall,
  subscribeInstallPrompt,
} from "../utils/pwaInstall";

// "Carry your card" dashboard section (D-007): add your own card to Apple
// Wallet, Google Wallet, or your phone's home screen.
//
// Every action is gated twice — on the platform it works on, and on whether
// this server is actually provisioned for it:
//
//   - Apple Wallet: iOS/iPadOS and macOS (a .pkpass is inert on Android), and
//     only when the server reports Apple credentials + a pass icon.
//   - Google Wallet: any platform (the save link is a Google web page), and
//     only when the server reports Google credentials.
//   - Home screen: a real one-tap prompt on Android; on iOS Apple exposes no
//     programmatic trigger, so the Share-sheet steps are the feature rather
//     than a fallback. Hidden once the app is already installed.
//
// Unconfigured actions are omitted, not disabled: a disabled button leaves the
// control in the layout but out of the tab order, telling a screen-reader user
// nothing about why. Better to not promise the action at all.

interface WalletSectionProps {
  cardId: string;
  card: HorizontalCardData;
  /** The public handle URL, or null when the account has no handle yet. */
  publicUrl: string | null;
  isPro?: boolean;
}

type Status = { kind: "error" | "info"; message: string } | null;

/**
 * iOS Safari ignores `download` for blob URLs and navigates instead, so
 * revoking immediately after click() can abort the Wallet handoff mid-flight.
 * Hold the URL well past any plausible handoff, and drop it early if the page
 * is hidden (the pass has been taken over by Wallet at that point).
 */
const BLOB_LIFETIME_MS = 60_000;

function revokeLater(objectUrl: string): void {
  let done = false;
  const revoke = () => {
    if (done) return;
    done = true;
    document.removeEventListener("visibilitychange", onHidden);
    URL.revokeObjectURL(objectUrl);
  };
  function onHidden() {
    if (document.visibilityState === "hidden") revoke();
  }
  document.addEventListener("visibilitychange", onHidden);
  setTimeout(revoke, BLOB_LIFETIME_MS);
}

const buttonClass =
  "inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const secondaryButtonClass =
  "border border-coffee-300 dark:border-coffee-700 text-coffee-800 dark:text-cream-100 hover:bg-coffee-50 dark:hover:bg-coffee-800";

const WalletSection: React.FC<WalletSectionProps> = ({ cardId, card, publicUrl, isPro = false }) => {
  const platform = detectPlatform();
  const installed = isStandalone();

  const [promptAvailable, setPromptAvailable] = useState(isInstallPromptAvailable());
  // Nothing is offered until the server says it can honor it.
  const [capabilities, setCapabilities] = useState<{ apple: boolean; google: boolean }>({
    apple: false,
    google: false,
  });
  const [busy, setBusy] = useState<"apple" | null>(null);
  const [status, setStatus] = useState<Status>(null);

  // Google's flow really is just a link, so resolve it up front and render a
  // real anchor. Opening a window after an await lands outside the user-gesture
  // window and Safari blocks it silently.
  const [googleSaveUrl, setGoogleSaveUrl] = useState<string | null>(null);

  // beforeinstallprompt can arrive after this mounts (or have arrived before).
  useEffect(() => {
    setPromptAvailable(isInstallPromptAvailable());
    return subscribeInstallPrompt(setPromptAvailable);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchWalletCapabilities()
      .then((next) => {
        if (!cancelled) setCapabilities({ apple: !!next?.apple, google: !!next?.google });
      })
      // An unreachable or unauthenticated probe means "offer nothing" — never
      // an error message about plumbing the user cannot act on.
      .catch(() => {
        if (!cancelled) setCapabilities({ apple: false, google: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const needsHandle = !publicUrl;
  const showApple = capabilities.apple && (platform === "ios" || platform === "macos");
  const showGoogle = capabilities.google;
  const showHomeScreen = !installed && (platform === "android" || platform === "ios");

  useEffect(() => {
    if (!showGoogle || needsHandle) {
      setGoogleSaveUrl(null);
      return;
    }
    let cancelled = false;
    fetchGoogleWalletLink(cardId)
      .then(({ saveUrl }) => {
        if (!cancelled) setGoogleSaveUrl(saveUrl);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setGoogleSaveUrl(null);
        setStatus({
          kind: "error",
          message:
            err instanceof Error ? err.message : "Could not build your Google Wallet link.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [cardId, showGoogle, needsHandle]);

  const handleApple = async () => {
    setBusy("apple");
    setStatus(null);
    try {
      const blob = await fetchAppleWalletPass(cardId);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = "whobrew-card.pkpass";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      revokeLater(objectUrl);
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Could not build your Wallet pass.",
      });
    } finally {
      setBusy(null);
    }
  };

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === "unavailable") {
      setStatus({
        kind: "info",
        message: "Install isn't available on this device yet.",
      });
    }
  };

  // With nothing to offer on this platform, the section is just noise.
  if (!showApple && !showGoogle && !showHomeScreen) return null;

  return (
    <section
      aria-labelledby="wallet-heading"
      className="bg-white dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-xl p-5"
    >
      <div className="mb-4">
        <h3 id="wallet-heading" className="text-lg font-semibold text-coffee-900 dark:text-cream-50">
          Carry your card
        </h3>
        <p className="mt-1 text-sm text-coffee-600 dark:text-cream-300">
          Keep your card in your wallet or on your home screen, so it's one tap away when you meet
          someone.
        </p>
      </div>

      <div className="mb-4">
        <HorizontalCard card={card} publicUrl={publicUrl || undefined} isPro={isPro} />
      </div>

      {needsHandle && (showApple || showGoogle) && (
        <p className="mb-4 text-sm text-coffee-700 dark:text-cream-200">
          Set your account handle in your{" "}
          <a href="/profile" className="underline font-medium">
            Profile
          </a>{" "}
          first — a wallet pass needs your public card link.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {showApple && (
          <button
            type="button"
            onClick={handleApple}
            disabled={needsHandle || busy === "apple"}
            className={`${buttonClass} bg-coffee-900 text-cream-50 hover:bg-coffee-800`}
          >
            {busy === "apple" ? "Building pass…" : "Add to Apple Wallet"}
          </button>
        )}

        {/* A real link, so the click needs no gesture window and cannot be
            popup-blocked. Rendered only once the save URL has resolved. */}
        {showGoogle && googleSaveUrl && (
          <a
            href={googleSaveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${buttonClass} ${secondaryButtonClass}`}
          >
            Add to Google Wallet
          </a>
        )}

        {showHomeScreen && platform === "android" && (
          <button
            type="button"
            onClick={handleInstall}
            className={`${buttonClass} ${secondaryButtonClass}`}
          >
            Add to Home Screen
          </button>
        )}
      </div>

      {/* Android: the prompt exists only once the browser considers the app
          installable, which today also depends on app-side assets. Say it is
          unavailable without blaming the user's browser for it. */}
      {showHomeScreen && platform === "android" && !promptAvailable && (
        <p className="mt-2 text-xs text-coffee-500 dark:text-cream-400">
          Install isn't available on this device yet. You can still add this page from your
          browser's menu.
        </p>
      )}

      {/* iOS: Apple exposes no programmatic install trigger, so these steps ARE
          the flow. Stated as a simple how-to, with no apology. */}
      {showHomeScreen && platform === "ios" && (
        <div className="mt-3 rounded-lg bg-coffee-50 dark:bg-coffee-800 border border-coffee-100 dark:border-coffee-700 px-3 py-2.5">
          <p className="text-xs font-medium text-coffee-800 dark:text-cream-100">
            Add to your iPhone home screen
          </p>
          <ol className="mt-1.5 space-y-1 text-xs text-coffee-700 dark:text-cream-200 list-decimal list-inside">
            <li>
              Tap the Share button{" "}
              <span aria-hidden="true" className="inline-block align-middle">
                {/* Safari's share glyph: a box with an upward arrow. */}
                <svg width="12" height="14" viewBox="0 0 12 14" fill="none" className="inline">
                  <path
                    d="M6 1v7M6 1L3.5 3.5M6 1l2.5 2.5"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 6.5V12a1 1 0 001 1h6a1 1 0 001-1V6.5"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{" "}
              in Safari's toolbar
            </li>
            <li>Scroll down and tap “Add to Home Screen”</li>
            <li>Tap “Add”</li>
          </ol>
        </div>
      )}

      {status && (
        <p
          role={status.kind === "error" ? "alert" : "status"}
          className={`mt-3 text-xs ${
            status.kind === "error" ? "text-danger-500" : "text-coffee-600 dark:text-cream-300"
          }`}
        >
          {status.message}
        </p>
      )}
    </section>
  );
};

export default WalletSection;
